import { useEffect, useRef, useState } from "react";

/**
 * 숫자가 «뚝» 바뀌지 않고 이전값 → 새값으로 부드럽게 카운트업 되게 하는 훅.
 * 목표값이 바뀌면 현재 표시값에서 새 목표값까지 duration(기본 400ms) 동안 애니메이션한다.
 */
export function useCountUp(target: number, duration = 400): number {
  const safeTarget = Number.isFinite(target) ? target : 0;
  const [value, setValue] = useState(safeTarget);
  const fromRef = useRef(safeTarget);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const from = fromRef.current;
    const to = safeTarget;

    if (from === to) {
      setValue(to);
      return;
    }

    let start: number | null = null;
    const frame = (ts: number) => {
      if (start === null) start = ts;
      const p = Math.min(1, (ts - start) / duration);
      // ease-out-cubic
      const eased = 1 - Math.pow(1 - p, 3);
      const current = Math.round(from + (to - from) * eased);
      setValue(current);
      // 매 프레임 «지금 보이는 값»을 기준점으로 갱신한다.
      // 목표값이 애니메이션 도중에 바뀌어도, 다음 애니메이션이
      // 마지막 완료값이 아니라 현재 화면값에서 이어지도록 한다.
      fromRef.current = current;
      if (p < 1) {
        rafRef.current = requestAnimationFrame(frame);
      } else {
        fromRef.current = to;
      }
    };

    rafRef.current = requestAnimationFrame(frame);

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [safeTarget, duration]);

  return value;
}